import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'stickyPrice', 'addToCartButton'];

  #abortController;
  #intersectionObserver;
  #formObserver;
  #targetButton;
  #variantLoading = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.cartError, this.#revealProductForm, { signal });
    section?.addEventListener('invalid', this.#revealProductForm, { signal, capture: true });
    section?.addEventListener('toggle', this.#syncVisibility, { signal, capture: true });
    this.#observeProductForm();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#formObserver?.disconnect();
    this.#abortController?.abort();
  }

  #getProductForm() {
    return this.closest('.shopify-section')?.querySelector('product-form-component');
  }

  #observeProductForm() {
    this.#formObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    const productForm = this.#getProductForm();
    this.#targetButton = productForm?.querySelector('[ref="addToCartButton"]');
    if (!productForm || !this.#targetButton) {
      this.#setVisible(false);
      return;
    }

    this.#intersectionObserver = new IntersectionObserver(this.#syncVisibility);
    this.#intersectionObserver.observe(this.#targetButton);
    this.#formObserver = new MutationObserver(() => {
      const currentButton = productForm.querySelector('[ref="addToCartButton"]');
      if (currentButton !== this.#targetButton) {
        this.#observeProductForm();
        return;
      }
      this.#syncButton();
    });
    this.#formObserver.observe(productForm, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });
    this.#syncButton();
    this.#syncVisibility();
  }

  #syncButton() {
    const button = this.refs.addToCartButton;
    button.disabled = this.#variantLoading || !this.#targetButton || this.#targetButton.disabled;
    const label = this.#targetButton?.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    if (label) button.textContent = label;
  }

  #handleVariantSelected = () => {
    this.#variantLoading = true;
    this.#syncButton();
  };

  #handleVariantUpdate = (event) => {
    const updatedBar = event.detail.data.html.querySelector('sticky-add-to-cart');
    const updatedPrice = updatedBar?.querySelector('[ref="stickyPrice"]');
    if (updatedPrice) this.refs.stickyPrice.innerHTML = updatedPrice.innerHTML;
    const updatedTitle = updatedBar?.querySelector('.sticky-add-to-cart__title');
    const title = this.querySelector('.sticky-add-to-cart__title');
    if (updatedTitle && title) title.textContent = updatedTitle.textContent;
    this.#variantLoading = false;
    queueMicrotask(() => {
      if (this.isConnected) this.#observeProductForm();
    });
  };

  handleAddToCartClick = () => {
    if (!this.#targetButton || this.#targetButton.disabled || this.#variantLoading) return;
    this.#openPanel();
    this.#targetButton.click();
  };

  #openPanel() {
    const panel = this.closest('.shopify-section')?.querySelector('.product-gallery-panel__details');
    if (panel instanceof HTMLDetailsElement) panel.open = true;
  }

  #revealProductForm = () => {
    this.#openPanel();
    this.#getProductForm()?.scrollIntoView({
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      block: 'center',
    });
  };

  #syncVisibility = () => {
    if (!this.#targetButton) {
      this.#setVisible(false);
      return;
    }
    const rect = this.#targetButton.getBoundingClientRect();
    const visible = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    this.#setVisible(!visible);
  };

  #setVisible(visible) {
    const bar = this.refs.stickyBar;
    bar.dataset.stuck = String(visible);
    bar.inert = !visible;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
